import React, { useEffect, useState } from 'react';
import { MapPinIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, ClockIcon, ArrowLeftIcon, TruckIcon, BuildingIcon, NavigationIcon, ShieldIcon } from 'lucide-react';
interface GeoClockInScreenProps {
  job: any;
  onClockIn: () => void;
  onCancel: () => void;
}
export const GeoClockInScreen = ({
  job,
  onClockIn,
  onCancel
}: GeoClockInScreenProps) => {
  const [currentLocation, setCurrentLocation] = useState<{
    lat: number | null;
    lng: number | null;
  }>({
    lat: null,
    lng: null
  });
  const [locationStatus, setLocationStatus] = useState<'checking' | 'allowed' | 'denied' | 'unavailable' | 'inRange' | 'outOfRange'>('checking');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  // Check if the geolocation API is available
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      setErrorMessage('Geolocation is not supported by your browser');
      return;
    }
    // Get current position
    navigator.geolocation.getCurrentPosition(position => {
      setCurrentLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
      setLocationStatus('allowed');
      // Calculate distance to job location (load location)
      if (job.loadLocation?.coordinates) {
        const distanceInFeet = calculateDistance(position.coords.latitude, position.coords.longitude, job.loadLocation.coordinates.lat, job.loadLocation.coordinates.lng);
        setDistance(distanceInFeet);
        // Check if driver is within the required distance
        if (job.geoPunchRequired) {
          if (distanceInFeet <= (job.geoPunchDistance || 150)) {
            setLocationStatus('inRange');
          } else {
            setLocationStatus('outOfRange');
            setErrorMessage(`You are ${Math.round(distanceInFeet)} feet away from the job location. You need to be within ${job.geoPunchDistance || 150} feet to clock in.`);
          }
        } else {
          // If geo punch is not required, driver can clock in from anywhere
          setLocationStatus('inRange');
        }
      }
    }, error => {
      setLocationStatus('denied');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setErrorMessage('Location access denied. Please enable location services for this site.');
          break;
        case error.POSITION_UNAVAILABLE:
          setErrorMessage('Location information is unavailable.');
          break;
        case error.TIMEOUT:
          setErrorMessage('The request to get location timed out.');
          break;
        default:
          setErrorMessage('An unknown error occurred while getting location.');
          break;
      }
    });
  }, [job]);
  // Function to calculate distance between two points in feet
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceInMeters = R * c;
    const distanceInFeet = distanceInMeters * 3.28084; // Convert meters to feet
    return distanceInFeet;
  };
  const handleClockIn = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      onClockIn();
      setLoading(false);
    }, 1000);
  };
  const handleNavigate = () => {
    // Open navigation to job location
    if (job.loadLocation?.coordinates) {
      const url = `https://www.google.com/maps/search/?api=1&query=${job.loadLocation.coordinates.lat},${job.loadLocation.coordinates.lng}`;
      window.open(url, '_blank');
    }
  };
  return <div className="fixed inset-0 bg-gray-100 z-50 overflow-y-auto" data-id="element-2286">
      <div className="max-w-md mx-auto p-4 pt-16" data-id="element-2287">
        <button onClick={onCancel} className="absolute top-4 left-4 p-2 text-gray-600 hover:text-gray-900" data-id="element-2288">
          <ArrowLeftIcon size={24} data-id="element-2289" />
        </button>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-2290">
          {/* Job Header */}
          <div className="p-4 bg-blue-50 border-b" data-id="element-2291">
            <h2 className="text-lg font-bold text-blue-900" data-id="element-2292">{job.jobName}</h2>
            <p className="text-sm text-blue-700" data-id="element-2293">Job #{job.jobNumber}</p>
          </div>
          {/* Location Status */}
          <div className="p-6" data-id="element-2294">
            <div className="flex flex-col items-center justify-center mb-6" data-id="element-2295">
              {locationStatus === 'checking' && <div className="animate-pulse" data-id="element-2296">
                  <div className="h-16 w-16 rounded-full bg-blue-200 flex items-center justify-center mb-3" data-id="element-2297">
                    <MapPinIcon size={32} className="text-blue-500" data-id="element-2298" />
                  </div>
                  <p className="text-center font-medium" data-id="element-2299">
                    Checking your location...
                  </p>
                </div>}
              {locationStatus === 'inRange' && <div className="text-center" data-id="element-2300">
                  <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-3 mx-auto" data-id="element-2301">
                    <CheckCircleIcon size={32} className="text-green-600" data-id="element-2302" />
                  </div>
                  <h3 className="text-lg font-bold text-green-700 mb-1" data-id="element-2303">
                    Location Verified
                  </h3>
                  <p className="text-sm text-gray-600 mb-2" data-id="element-2304">
                    {job.geoPunchRequired ? `You are within the required ${job.geoPunchDistance || 150} feet of the job location.` : 'Location verification is not required for this job.'}
                  </p>
                  {distance && <p className="text-xs text-gray-500" data-id="element-2305">
                      Current distance: ~{Math.round(distance)} feet from job
                      site
                    </p>}
                </div>}
              {locationStatus === 'outOfRange' && <div className="text-center" data-id="element-2306">
                  <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-3 mx-auto" data-id="element-2307">
                    <AlertTriangleIcon size={32} className="text-red-600" data-id="element-2308" />
                  </div>
                  <h3 className="text-lg font-bold text-red-700 mb-1" data-id="element-2309">
                    Not at Job Location
                  </h3>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-2310">{errorMessage}</p>
                  <button onClick={handleNavigate} className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center gap-2 mx-auto" data-id="element-2311">
                    <NavigationIcon size={16} data-id="element-2312" />
                    Navigate to Job Site
                  </button>
                </div>}
              {(locationStatus === 'denied' || locationStatus === 'unavailable') && <div className="text-center" data-id="element-2313">
                  <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-3 mx-auto" data-id="element-2314">
                    <XCircleIcon size={32} className="text-red-600" data-id="element-2315" />
                  </div>
                  <h3 className="text-lg font-bold text-red-700 mb-1" data-id="element-2316">
                    Location Access Required
                  </h3>
                  <p className="text-sm text-gray-600 mb-2" data-id="element-2317">{errorMessage}</p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-2318">
                    Please enable location services and refresh the page.
                  </p>
                </div>}
            </div>
            {/* Job Location Info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6" data-id="element-2319">
              <h3 className="font-medium mb-2 flex items-center gap-2" data-id="element-2320">
                <MapPinIcon size={16} className="text-blue-600" data-id="element-2321" />
                Job Location
              </h3>
              <p className="font-medium" data-id="element-2322">{job.loadLocation?.name}</p>
              <p className="text-sm text-gray-600 mb-2" data-id="element-2323">
                {job.loadLocation?.address}
              </p>
              <button onClick={handleNavigate} className="text-sm text-blue-600 flex items-center gap-1" data-id="element-2324">
                <NavigationIcon size={14} data-id="element-2325" />
                Get Directions
              </button>
            </div>
            {/* Geo Punch Policy */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6" data-id="element-2326">
              <h3 className="font-medium mb-2 flex items-center gap-2" data-id="element-2327">
                <ShieldIcon size={16} className="text-blue-600" data-id="element-2328" />
                Geo Punch Policy
              </h3>
              <p className="text-sm text-gray-600" data-id="element-2329">
                {job.geoPunchRequired ? `This job requires you to be within ${job.geoPunchDistance || 150} feet of the job location to clock in and out.` : 'Location verification is not required for this job, but your location will still be recorded.'}
              </p>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="p-4 border-t bg-gray-50" data-id="element-2330">
            <div className="grid grid-cols-2 gap-3" data-id="element-2331">
              <button onClick={onCancel} className="px-4 py-3 border border-gray-300 text-gray-700 rounded-md font-medium" data-id="element-2332">
                Cancel
              </button>
              <button onClick={handleClockIn} disabled={locationStatus !== 'inRange' || loading} className={`px-4 py-3 rounded-md font-medium flex items-center justify-center gap-2 ${locationStatus === 'inRange' && !loading ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-2333">
                {loading ? <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-2334"></div>
                    Processing...
                  </> : <>
                    <ClockIcon size={18} data-id="element-2335" />
                    Clock In
                  </>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};